import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { createPortal } from "react-dom";
import {
  X, Sparkles, Check, Gift, Cake, Flower2, Heart,
  MapPin, ShoppingBag, PartyPopper, Wand2, Clock, HeartHandshake,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { addToCart } from "@/lib/cart";
import { PlanProductCard, ChangeProductDialog } from "@/components/PlannerProductCard";
import { AIPlanIdentityHeader, CopyMessageButton } from "@/components/PlannerResultBits";
import { personalTags } from "@/lib/planPersonalization";

const OCCASIONS = ["Birthday", "Anniversary", "Other"];

// If an entry point passed a context occasion that isn't in our 3 default
// chips, surface it as its own chip so Gemini receives the exact label and
// the customer sees their intent reflected on step 1.
function _occasionChips(defaultOccasion) {
  const d = String(defaultOccasion || "").trim();
  if (d && !OCCASIONS.includes(d)) return [d, ...OCCASIONS];
  return OCCASIONS;
}
const STYLES = ["Simple", "Romantic", "Family", "Luxury"];
const PINCODE_KEY = "celebrai_pincode";

const CATEGORY_ICON = {
  flowers: Flower2,
  cakes: Cake,
  decorations: Heart,
  hampers: Gift,
  experience: PartyPopper,
  personalisation: Wand2,
};


export default function PlannerModal({ relationship, defaultOccasion = "Birthday", defaultExperienceId = null, defaultStyle = null, defaultTier = null, onClose }) {
  const [step, setStep] = useState(0); // 0=landing 1=occasion 2=mood 3=pincode 4=result 5=pending
  const [occasion, setOccasion] = useState(defaultOccasion);
  const [style, setStyle] = useState(() =>
    defaultStyle && STYLES.includes(defaultStyle) ? defaultStyle : "Romantic");
  const [pincode, setPincode] = useState(() => localStorage.getItem(PINCODE_KEY) || "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);      // full doc with plans[]
  const [activeTier, setActiveTier] = useState("Experience");
  const [selected, setSelected] = useState({});   // { [tier]: { [element_index]: bool } }
  const [lastPlan, setLastPlan] = useState(null);
  const [lastLoading, setLastLoading] = useState(true);
  const [experiences, setExperiences] = useState([]);
  const [selectedExperiences, setSelectedExperiences] = useState({}); // {experience_id: true}
  // Custom Budget lives on the result screen as a fallback: after seeing the
  // 3 standard plans a customer can enter their own Min/Max range to regenerate.
  const [customMin, setCustomMin] = useState("");
  const [customMax, setCustomMax] = useState("");
  // Both plan sets persist for the modal session so the customer can toggle
  // between original (standard) and custom plans without regenerating.
  const [standardResult, setStandardResult] = useState(null);   // original_plans
  const [customResult, setCustomResult] = useState(null);       // custom_plans
  const navigate = useNavigate();

  // Full-screen planner: lock background scroll while open, restore on close.
  useEffect(() => {
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    document.body.classList.add("planner-open");
    return () => {
      document.body.style.overflow = overflow;
      document.body.classList.remove("planner-open");
    };
  }, []);

  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  // Load enabled experiences from admin config (always show, independent of AI).
  useEffect(() => {
    api.get("/experiences").then((r) => {
      const list = r.data || [];
      setExperiences(list);
      // Entry-point context: pre-tick the experience the customer tapped on
      // the Home "Make It More Memorable" section so they don't repeat intent.
      if (defaultExperienceId && list.some((e) => e.id === defaultExperienceId)) {
        setSelectedExperiences((prev) => ({ ...prev, [defaultExperienceId]: true }));
      }
    }).catch(() => {});
  }, [defaultExperienceId]);

  // Build per-tier default selections (all products with a matched product ticked; unavailable skipped).
  const buildDefaultSelection = (planDoc) => {
    const sel = {};
    (planDoc?.plans || []).forEach((tp) => {
      const tierSel = {};
      (tp.availability || []).forEach((a) => {
        // Results show inventory products only, so AI-only (sourceable) ideas aren't pre-selected.
        if (a.match_kind === "sourceable") return;
        if (a.product && a.match_kind !== "none" && a.delivery?.tag !== "unavailable") {
          tierSel[a.element_index] = true;
        }
      });
      sel[tp.tier] = tierSel;
    });
    return sel;
  };

  const chooseDefaultTier = (planDoc) => {
    const tiers = (planDoc?.plans || []).map((p) => p.tier);
    // Hero "Budget" pill context: pre-select the chosen tier tab on the result
    // (keys are lowercase in the tier config, plan tiers are capitalized).
    if (defaultTier) {
      const match = tiers.find((t) => t.toLowerCase() === String(defaultTier).toLowerCase());
      if (match) return match;
    }
    if (tiers.includes("Experience")) return "Experience";
    return tiers[0] || "Experience";
  };

  // On open: check for a previously saved inspiration for this person.
  useEffect(() => {
    let alive = true;
    setLastLoading(true);
    api.get(`/planner/last-inspiration/${relationship.id}`)
      .then((r) => {
        if (!alive) return;
        const data = r.data;
        if (!data) { setStep(1); return; }
        // A pending request from a previous visit → show the friendly waiting screen.
        if (data.status === "PENDING" || data.status === "GENERATING") {
          setResult(data);
          setStep(5);
          return;
        }
        if (Array.isArray(data.plans) && data.plans.length > 0) {
          setLastPlan(data);
          setStep(0);
        } else {
          setStep(1);
        }
      })
      .catch(() => { if (alive) setStep(1); })
      .finally(() => { if (alive) setLastLoading(false); });
    return () => { alive = false; };
  }, [relationship.id]);

  const viewLastPlan = () => {
    if (!lastPlan) return;
    setResult(lastPlan);
    setSelected(buildDefaultSelection(lastPlan));
    setActiveTier(chooseDefaultTier(lastPlan));
    if (lastPlan.budget_tier_key !== "custom") setStandardResult(lastPlan);
    else setCustomResult(lastPlan);
    if (lastPlan.pincode) setPincode(String(lastPlan.pincode));
    if (lastPlan.occasion) setOccasion(lastPlan.occasion);
    if (lastPlan.style) setStyle(lastPlan.style);
    setStep(4);
  };

  const generate = async ({ force = false, customBudget = null } = {}) => {
    if (!/^\d{6}$/.test(pincode)) { toast.error("Enter a valid 6-digit pincode"); return; }
    // Custom Budget regen (fallback CTA on the result screen). Validate range.
    if (customBudget) {
      const lo = Number(customBudget.min), hi = Number(customBudget.max);
      if (!lo || !hi || lo <= 0 || hi <= 0 || hi < lo) {
        toast.error("Enter a valid custom range (Max ≥ Min)"); return;
      }
    }
    localStorage.setItem(PINCODE_KEY, pincode);
    setLoading(true);
    try {
      const payload = {
        relationship_id: relationship.id,
        occasion, style, pincode, force,
      };
      if (customBudget) {
        payload.budget_tier_key = "custom";
        payload.budget_min = Number(customBudget.min);
        payload.budget_max = Number(customBudget.max);
      }
      const res = await api.post("/planner/inspire", payload);
      const data = res.data;
      setResult(data);
      // PENDING → show the friendly waiting screen. No plans yet.
      if (data?.status === "PENDING") {
        setStep(5);
        return;
      }
      setSelected(buildDefaultSelection(data));
      setActiveTier(chooseDefaultTier(data));
      setStep(4);
      // Standard plans are always the safe fallback to return to.
      if (!customBudget) setStandardResult(data);
      else setCustomResult(data);
      if (data?.from_cache) {
        toast.success("We found a plan for this exact request. Showing your saved plan.");
      } else if (customBudget) {
        toast.success("Fresh plans generated for your budget.");
      } else if (force) {
        toast.success("Fresh plan generated.");
      }
    } catch (err) {
      // Never show technical errors to the user — always fall back to the pending screen.
      setResult({ status: "PENDING" });
      setStep(5);
    } finally { setLoading(false); }
  };

  const activePlan = useMemo(() => {
    if (!result?.plans) return null;
    return result.plans.find((p) => p.tier === activeTier) || result.plans[0] || null;
  }, [result, activeTier]);

  const tierSelected = selected[activeTier] || {};

  const setTierSelection = (updater) => {
    setSelected((s) => {
      const tierSel = s[activeTier] || {};
      const next = typeof updater === "function" ? updater(tierSel) : updater;
      return { ...s, [activeTier]: next };
    });
  };

  const availableCount = useMemo(() => {
    if (!activePlan) return 0;
    return (activePlan.availability || []).filter((a) => tierSelected[a.element_index]).length;
  }, [activePlan, tierSelected]);

  const pickedExperiences = useMemo(
    () => experiences.filter((e) => selectedExperiences[e.id]),
    [experiences, selectedExperiences]
  );

  // ---- Hard budget ceiling (Custom Budget) ----
  const budgetCeiling = useMemo(() => {
    if (result?.budget_tier_key === "custom" && result?.budget_max) {
      return Number(result.budget_max);
    }
    return null;
  }, [result]);

  // Plan-level invalid = full matched plan (products + one delivery fee)
  // exceeds the ceiling. Computed LIVE from the current rows so it self-heals
  // after a trending-alternative swap (backend validation equals this pre-swap).
  const activePlanInvalid = useMemo(() => {
    if (!budgetCeiling || !activePlan) return false;
    let sum = 0;
    let fee = 0;
    (activePlan.availability || []).forEach((a) => {
      if (a.product) {
        sum += Number(a.product.price || 0);
        fee = Math.max(fee, Number(a.delivery?.fee || 0));
      }
    });
    return sum + fee > budgetCeiling;
  }, [budgetCeiling, activePlan]);

  // Live payable total for the CURRENTLY TICKED items (products + one delivery
  // fee + confirmed-price experiences). Sourceable / on-request items excluded.
  const selectedPayable = useMemo(() => {
    if (!activePlan) return 0;
    let sum = 0;
    let fee = 0;
    (activePlan.availability || []).forEach((a) => {
      if (tierSelected[a.element_index] && a.product) {
        sum += Number(a.product.price || 0);
        fee = Math.max(fee, Number(a.delivery?.fee || 0));
      }
    });
    pickedExperiences.forEach((e) => {
      if (e.price_type !== "on_request") sum += Number(e.price || 0);
    });
    return sum + fee;
  }, [activePlan, tierSelected, pickedExperiences]);

  const selectedOverBudget = !!(budgetCeiling && selectedPayable > budgetCeiling);

  // "View Original Plans" — restore the standard plans without a Gemini call.
  const viewOriginalPlans = () => {
    if (standardResult) {
      setResult(standardResult);
      setSelected(buildDefaultSelection(standardResult));
      setActiveTier(chooseDefaultTier(standardResult));
      toast.success("Showing your original plans.");
    } else {
      generate({ force: false });
    }
  };

  // "View Custom Plans" — switch back to the custom-generated plans, also
  // without regenerating (both sets persist for the modal session).
  const viewCustomPlans = () => {
    if (!customResult) return;
    setResult(customResult);
    setSelected(buildDefaultSelection(customResult));
    setActiveTier(chooseDefaultTier(customResult));
    toast.success("Showing your custom budget plans.");
  };

  // Swap ONE availability row to its trending alternative. Local state only —
  // never regenerates the plan; selection tick state is preserved.
  const handleSwap = (tier, elementIndex, alt) => {
    setResult((prev) => {
      if (!prev?.plans) return prev;
      const ceiling = prev.budget_tier_key === "custom" && prev.budget_max
        ? Number(prev.budget_max) : null;
      const plans = prev.plans.map((pl) => {
        if (pl.tier !== tier) return pl;
        const availability = (pl.availability || []).map((a) => {
          if (a.element_index !== elementIndex || !a.product) return a;
          const price = Number(alt.product.price || 0);
          return {
            ...a,
            product: alt.product,
            match_kind: alt.match_kind || a.match_kind,
            delivery: alt.delivery || a.delivery,
            above_budget: ceiling ? price > ceiling : false,
            over_by: ceiling ? Math.max(0, price - ceiling) : 0,
            product_is_trending: alt.is_trending ?? true,
            user_changed: !!alt.user_changed,
            trending_alternative: null,
          };
        });
        return { ...pl, availability };
      });
      return { ...prev, plans };
    });
    toast.success(`Swapped to ${alt.product.name}`);
  };

  const toggleExperience = (id) =>
    setSelectedExperiences((s) => ({ ...s, [id]: !s[id] }));

  const bookAvailable = () => {
    const availItems = activePlan ? (activePlan.availability || []) : [];
    const picked = availItems.filter(
      (a) => tierSelected[a.element_index] && a.product && a.match_kind !== "sourceable"
    );
    const sourceablePicked = availItems.filter(
      (a) => tierSelected[a.element_index] && a.match_kind === "sourceable"
    );
    if (picked.length === 0 && sourceablePicked.length === 0 && pickedExperiences.length === 0) {
      toast.error("Select at least one item or add-on"); return;
    }
    picked.forEach((a) => addToCart({
      kind: "product",
      product_id: a.product.id,
      name: a.product.name,
      category: a.product.category,
      price: a.product.price,
      quantity: 1,
      image_url: a.product.image_url || (a.product.images && a.product.images[0]) || "",
      is_global: !!a.product.is_global,
      delivery_pincode: a.product.delivery_pincode != null ? String(a.product.delivery_pincode).trim() : null,
    }));
    sourceablePicked.forEach((a) => addToCart({
      kind: "sourceable",
      line_key: `src:${a.element_index}:${(a.element.name || "item").slice(0, 40)}`,
      name: a.element.name,
      category: a.element.category || "sourceable",
      source_description: a.element.description || "",
      price: 0,
      price_type: "on_request",
      quantity: 1,
      is_global: true,
      delivery_pincode: null,
    }));
    pickedExperiences.forEach((e) => addToCart({
      kind: "experience",
      experience_id: e.id,
      name: e.name,
      category: "experience",
      price: e.price || 0,
      price_type: e.price_type || "fixed",
      quantity: 1,
      image_url: e.image_url || "",
      is_global: true,          // experiences never trigger delivery fees
      delivery_pincode: null,
    }));
    const totalAdded = picked.length + sourceablePicked.length + pickedExperiences.length;
    // Persist the customer's budget selection so Checkout can attach it to the
    // booking payload (and admin sees it on the order). Only populated when the
    // customer regenerated with a Custom Budget from the result screen.
    const budgetForBooking = result?.budget_tier_key
      ? { tier_key: result.budget_tier_key,
          label: result.budget_label || null,
          min: result.budget_min ?? null, max: result.budget_max ?? null }
      : null;
    if (budgetForBooking) {
      try { localStorage.setItem("celebrai_cart_budget", JSON.stringify(budgetForBooking)); }
      catch { /* localStorage unavailable */ }
    }
    toast.success(`${totalAdded} item${totalAdded > 1 ? "s" : ""} added to cart`);
    onClose();
    navigate(
      `/checkout?relationship_id=${relationship.id}&occasion=${encodeURIComponent(occasion)}&pincode=${pincode}`
    );
  };

  // Portal to <body> so no transformed page wrapper can offset the full-screen layer.
  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-white"
        data-testid="planner-modal">
        <motion.div
          initial={{ y: 16 }} animate={{ y: 0 }} exit={{ y: 16 }}
          transition={{ type: "spring", stiffness: 260, damping: 26 }}
          className="bg-white w-full h-[100dvh] overflow-hidden flex flex-col">

          <header className="px-4 py-3 sm:py-4 sm:px-[max(1.5rem,calc((100%-48rem)/2))] pt-[max(0.75rem,env(safe-area-inset-top))] border-b border-warm flex items-center justify-between bg-white gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className={`w-10 h-10 rounded-2xl rose-glow flex items-center justify-center flex-shrink-0 ${loading ? "ai-glow" : ""}`}>
                <Sparkles className="w-5 h-5 text-white" strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold gradient-text-rose-purple truncate">AI Celebration Planner</p>
                <p className="font-display text-lg sm:text-xl text-charcoal leading-tight truncate">For {relationship.name}</p>
              </div>
            </div>
            <button onClick={onClose} className="rounded-full p-2 hover:bg-linen-2 flex-shrink-0" data-testid="planner-close"><X className="w-5 h-5" /></button>
          </header>

          <div className="px-4 py-4 sm:py-6 sm:px-[max(1.5rem,calc((100%-48rem)/2))] overflow-y-auto flex-1">
            {loading ? (
              <div className="py-20 text-center">
                <div className="w-16 h-16 rounded-2xl rose-glow mx-auto mb-6 ai-glow flex items-center justify-center">
                  <Sparkles className="w-7 h-7 text-white" strokeWidth={2} />
                </div>
                <p className="font-display text-2xl text-charcoal">Designing something special…</p>
                <p className="text-charcoal/60 mt-2">Layer 1: Creative plan · Layer 2: Availability for your pincode</p>
              </div>
            ) : lastLoading ? (
              <div className="py-20 text-center text-charcoal/60">Checking your saved plans…</div>
            ) : (
              <>
                {step === 0 && lastPlan && (
                  <LandingStep
                    lastPlan={lastPlan}
                    onView={viewLastPlan}
                    onGenerateNew={() => setStep(1)}
                    personName={relationship.name}
                  />
                )}
                {step === 1 && (
                  <StepChoice title="What are we celebrating?" options={_occasionChips(defaultOccasion)}
                    value={occasion} onChange={setOccasion} testKey="occasion" />
                )}
                {step === 2 && (
                  <StepChoice title="Pick the mood" options={STYLES}
                    value={style} onChange={setStyle} testKey="style" />
                )}
                {step === 3 && (
                  <PincodeStep pincode={pincode} setPincode={setPincode} />
                )}
                {step === 4 && result && activePlan && (
                  <ResultView
                    relationship={relationship}
                    plans={result.plans}
                    activePlan={activePlan}
                    activeTier={activeTier}
                    setActiveTier={setActiveTier}
                    pincode={pincode}
                    selected={tierSelected}
                    setSelected={setTierSelection}
                    showReadyHeader={result?.status === "READY" && (result?.from_cache || !!lastPlan)}
                    experiences={experiences}
                    selectedExperiences={selectedExperiences}
                    onToggleExperience={toggleExperience}
                    budget={result?.budget_tier_key ? {
                      tier_key: result.budget_tier_key,
                      min: result.budget_min, max: result.budget_max,
                    } : null}
                    customMin={customMin} setCustomMin={setCustomMin}
                    customMax={customMax} setCustomMax={setCustomMax}
                    onCustomBudgetRegenerate={(range) =>
                      generate({ force: true, customBudget: range })}
                    regenLoading={loading}
                    planInvalid={activePlanInvalid}
                    onViewOriginal={viewOriginalPlans}
                    hasStandardResult={!!standardResult}
                    onViewCustom={viewCustomPlans}
                    hasCustomResult={!!customResult}
                    onSwap={handleSwap}
                  />
                )}
                {step === 5 && (
                  <PendingScreen />
                )}
              </>
            )}
          </div>

          <footer className="px-4 pt-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))] sm:py-3 sm:px-[max(1.5rem,calc((100%-48rem)/2))] border-t border-warm flex items-center justify-between bg-linen-2 flex-wrap gap-2">
            <p className="text-xs text-charcoal/60 min-w-0 basis-full sm:basis-auto sm:flex-1">
              {step === 0 ? "Pick up where you left off" :
                step === 5 ? "We'll be ready in a moment" :
                step === 4 ? (
                  activePlanInvalid
                    ? "This plan couldn't be matched within your budget — pick an option below"
                    : budgetCeiling
                      ? `${activeTier} · ₹${selectedPayable.toLocaleString("en-IN")} of ₹${budgetCeiling.toLocaleString("en-IN")}${selectedOverBudget ? " — over budget" : ""}`
                      : `${activeTier} · ${availableCount + pickedExperiences.length} item${(availableCount + pickedExperiences.length) === 1 ? "" : "s"} selected`
                ) :
                `Step ${step} of 3`}
            </p>
            <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
              {step > 1 && step < 4 && !loading && (
                <button onClick={() => setStep(step - 1)} className="pill-btn pill-btn-secondary text-sm" data-testid="planner-back">Back</button>
              )}
              {step === 1 && !loading && lastPlan && (
                <button onClick={() => setStep(0)} className="pill-btn pill-btn-secondary text-sm" data-testid="planner-back-landing">Back</button>
              )}
              {step === 1 && !loading && (
                <button onClick={() => setStep(2)} className="pill-btn pill-btn-primary text-sm" data-testid="planner-next">Continue</button>
              )}
              {step === 2 && !loading && (
                <button onClick={() => setStep(3)} className="pill-btn pill-btn-primary text-sm" data-testid="planner-next-2">Continue</button>
              )}
              {step === 3 && !loading && (
                <button onClick={() => generate({ force: false })}
                  disabled={!/^\d{6}$/.test(pincode)}
                  className="rounded-full px-6 py-3 text-sm font-bold text-white shadow-lg shadow-rose-500/40 hover:-translate-y-0.5 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:transform-none"
                  style={{ background: 'linear-gradient(135deg, #E11D48 0%, #A855F7 100%)' }}
                  data-testid="planner-generate">
                  <Sparkles className="w-4 h-4" strokeWidth={2.25} /> Generate plan
                </button>
              )}
              {step === 5 && !loading && (
                <button onClick={onClose} className="pill-btn pill-btn-primary text-sm" data-testid="pending-close">
                  Got it, I&apos;ll come back
                </button>
              )}
              {step === 4 && !loading && (
                <>
                  {result?.from_cache ? (
                    <button onClick={() => generate({ force: true })}
                      className="pill-btn pill-btn-secondary !py-2 !px-4 !text-xs sm:!text-sm flex items-center justify-center gap-1.5 flex-1 sm:flex-none"
                      data-testid="generate-new-anyway">
                      <Sparkles className="w-3.5 h-3.5" strokeWidth={2.25} /> <span className="truncate">Generate New</span>
                    </button>
                  ) : (
                    <button onClick={() => { setStep(1); setResult(null); }} className="pill-btn pill-btn-secondary !py-2 !px-4 !text-xs sm:!text-sm flex-1 sm:flex-none" data-testid="planner-regen">
                      Try again
                    </button>
                  )}
                  {activePlanInvalid ? (
                    <span
                      className="text-xs font-semibold text-amber-700 self-center text-center flex-1 sm:flex-none px-2"
                      data-testid="book-blocked-invalid"
                    >
                      Plan over budget — adjust below
                    </span>
                  ) : (
                    <button onClick={bookAvailable}
                      disabled={(availableCount === 0 && pickedExperiences.length === 0) || selectedOverBudget}
                      className="rounded-full px-4 sm:px-5 py-2 text-xs sm:text-sm font-bold text-white shadow-md shadow-rose-500/30 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed flex-1 sm:flex-none"
                      style={{ background: 'linear-gradient(135deg, #E11D48 0%, #A855F7 100%)' }}
                      data-testid="book-available">
                      <ShoppingBag className="w-3.5 h-3.5" strokeWidth={2.25} />
                      <span className="truncate">
                        {selectedOverBudget
                          ? `Over budget — untick items`
                          : `Book Available (${availableCount + pickedExperiences.length})`}
                      </span>
                    </button>
                  )}
                </>
              )}
            </div>
          </footer>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

function LandingStep({ lastPlan, onView, onGenerateNew, personName }) {
  // Preview the "Experience" tier if present, else the middle plan, else the first.
  const plans = lastPlan.plans || [];
  const preview = plans.find((p) => p.tier === "Experience") || plans[Math.floor(plans.length / 2)] || plans[0] || {};
  const ins = preview.inspiration || {};
  const avail = preview.availability || [];
  const readyCount = avail.filter((a) => a.product && a.delivery?.tag !== "unavailable").length;

  const created = lastPlan.created_at ? new Date(lastPlan.created_at) : null;
  const dateStr = created
    ? created.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    : "";

  return (
    <div data-testid="planner-landing" className="space-y-6">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] font-bold gradient-text-rose-purple mb-2">
          Welcome back
        </p>
        <h3 className="font-display text-2xl sm:text-3xl text-charcoal leading-tight">
          You already have a plan for {personName}.
        </h3>
        <p className="text-charcoal/60 mt-2">
          Pick up where you left off — {plans.length > 1 ? `all ${plans.length} tiers are saved` : "the last plan is saved"}. Or start fresh with a brand new idea.
        </p>
      </div>

      <div className="rounded-3xl p-5 sm:p-6 bg-gradient-to-br from-rose-50 to-violet-50 border border-white/60"
        data-testid="landing-plan-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-bold text-charcoal/50">
                <Clock className="w-3 h-3" /> Saved {dateStr}
              </span>
              {lastPlan.pincode && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-bold text-charcoal/50">
                  <MapPin className="w-3 h-3" /> {lastPlan.pincode}
                </span>
              )}
              {plans.length > 0 && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-bold text-rose-600">
                  {plans.map((p) => p.tier).join(" · ")}
                </span>
              )}
            </div>
            <p className="font-display text-xl sm:text-2xl text-charcoal leading-tight truncate">
              {ins.title || "Your last celebration plan"}
            </p>
            <p className="text-sm text-charcoal/70 mt-1 line-clamp-2">
              {ins.ai_reasoning}
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-[10px] uppercase tracking-widest text-charcoal/60">Estimated ({preview.tier})</p>
            <p className="font-display text-2xl sm:text-3xl font-bold text-rose-600">
              ₹{(ins.estimated_budget || 0).toLocaleString("en-IN")}
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          {(ins.elements || []).slice(0, 5).map((el, i) => {
            const Icon = CATEGORY_ICON[el.category] || Sparkles;
            return (
              <span key={i}
                className="inline-flex items-center gap-1.5 rounded-full bg-white/70 border border-white px-2.5 py-1 text-[11px] font-medium text-charcoal/80">
                <Icon className="w-3 h-3 text-rose-500" strokeWidth={2} />
                {el.name}
              </span>
            );
          })}
          {(ins.elements || []).length > 5 && (
            <span className="text-[11px] text-charcoal/50 font-medium">
              +{ins.elements.length - 5} more
            </span>
          )}
        </div>

        <p className="text-xs text-charcoal/60 mt-4">
          {readyCount > 0
            ? `${readyCount} item${readyCount === 1 ? "" : "s"} ready to book from your inventory match.`
            : "No items were matched to inventory last time — try a new plan."}
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <button onClick={onView}
          className="flex-1 rounded-full px-6 py-3 text-sm font-bold text-white shadow-lg shadow-rose-500/40 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2"
          style={{ background: 'linear-gradient(135deg, #E11D48 0%, #A855F7 100%)' }}
          data-testid="view-last-plan">
          <Clock className="w-4 h-4" strokeWidth={2.25} /> View Last Plan
        </button>
        <button onClick={onGenerateNew}
          className="flex-1 pill-btn pill-btn-secondary text-sm flex items-center justify-center gap-2"
          data-testid="generate-new-plan">
          <Sparkles className="w-4 h-4" strokeWidth={2.25} /> Generate New Plan
        </button>
      </div>
    </div>
  );
}

function StepChoice({ title, options, value, onChange, testKey }) {
  return (
    <div>
      <h3 className="font-display text-2xl sm:text-3xl text-charcoal mb-8">{title}</h3>
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        {options.map((opt) => (
          <button key={opt} onClick={() => onChange(opt)} data-testid={`planner-${testKey}-${opt.toString().toLowerCase().replace(/[^\w]/g, "")}`}
            className={`rounded-2xl p-5 sm:p-6 text-left border-2 transition-all ${
              value === opt ? "border-rose-400 bg-rose-50 shadow-md" : "border-warm bg-white hover:border-charcoal/30"}`}>
            <div className="flex items-center justify-between">
              <span className="font-display text-lg sm:text-xl text-charcoal">{opt}</span>
              {value === opt && (
                <div className="w-7 h-7 rounded-full rose-glow flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" strokeWidth={3} />
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function PincodeStep({ pincode, setPincode }) {
  return (
    <div data-testid="planner-pincode-step">
      <div className="w-14 h-14 rounded-2xl rose-glow flex items-center justify-center mb-5">
        <MapPin className="w-6 h-6 text-white" strokeWidth={2} />
      </div>
      <h3 className="font-display text-2xl sm:text-3xl text-charcoal mb-2">Confirm your delivery pincode</h3>
      <p className="text-charcoal/60 mb-6 max-w-md">
        We use this to check which items are actually deliverable to your area and to calculate delivery fees.
      </p>
      <label className="text-xs uppercase tracking-[0.2em] font-semibold text-charcoal/60 mb-2 block">
        Delivery pincode
      </label>
      <input
        autoFocus type="text" inputMode="numeric" maxLength={6} pattern="\d{6}"
        placeholder="e.g. 302001"
        value={pincode}
        onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        className="w-full sm:w-56 rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400 text-lg font-semibold tracking-widest"
        data-testid="planner-pincode-input"
      />
      <p className="text-xs text-charcoal/50 mt-3">We&apos;ll remember this for next time.</p>
    </div>
  );
}

const TIER_META = {
  Essential: { label: "Essential", short: "Simple & sweet" },
  Experience: { label: "Experience", short: "A memorable evening" },
  Grand: { label: "Grand", short: "Go all out" },
};

function PendingScreen() {
  return (
    <div className="py-12 sm:py-16 text-center max-w-2xl mx-auto" data-testid="pending-screen">
      <div className="w-20 h-20 rounded-3xl mx-auto mb-6 bg-gradient-to-br from-rose-100 to-violet-100 flex items-center justify-center">
        <HeartHandshake className="w-9 h-9 text-rose-500" strokeWidth={1.75} />
      </div>
      <h3 className="font-display text-2xl sm:text-3xl text-charcoal leading-tight mb-3">
        We&apos;ve received your celebration details <span className="text-rose-500">❤️</span>
      </h3>
      <p className="text-charcoal/70 leading-relaxed">
        Our AI planner is temporarily busy. We&apos;re preparing your personalized celebration plan.
      </p>
      <p className="text-charcoal/70 mt-3 leading-relaxed">
        You don&apos;t need to submit your details again. You can leave this page and come back later.
        Your plan will appear automatically when it&apos;s ready.
      </p>
    </div>
  );
}

function ResultView({ relationship, plans, activePlan, activeTier, setActiveTier, pincode, selected, setSelected, showReadyHeader, experiences, selectedExperiences, onToggleExperience, budget, customMin, setCustomMin, customMax, setCustomMax, onCustomBudgetRegenerate, regenLoading, planInvalid, onViewOriginal, hasStandardResult, onViewCustom, hasCustomResult, onSwap }) {
  const ins = activePlan.inspiration || {};
  const availability = activePlan.availability || [];
  const isCustomBudget = budget?.tier_key === "custom" && budget?.min != null && budget?.max != null;
  const [budgetPanelOpen, setBudgetPanelOpen] = useState(false);
  const validation = activePlan?.budget_validation || null;

  // Live full-plan total (updates after trending-alternative swaps).
  const livePlanTotal = useMemo(() => {
    let sum = 0;
    let fee = 0;
    availability.forEach((a) => {
      if (a.product) {
        sum += Number(a.product.price || 0);
        fee = Math.max(fee, Number(a.delivery?.fee || 0));
      }
    });
    return sum + fee;
  }, [availability]);
  const liveOverBy = budget?.max != null ? Math.max(0, livePlanTotal - Number(budget.max)) : 0;

  const openBudgetPanel = () => {
    setBudgetPanelOpen(true);
    setTimeout(() => {
      document.querySelector('[data-testid="custom-budget-panel"]')
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  // Inventory-only view: real matched products (no AI concepts / estimates).
  const productRows = useMemo(
    () => availability.filter((a) => a.product && a.match_kind !== "sourceable" && a.match_kind !== "none"),
    [availability]
  );
  const [changeRow, setChangeRow] = useState(null);
  const personal = useMemo(() => personalTags(productRows, relationship), [productRows, relationship]);

  const pickReplacement = (p, delivery) => {
    const row = changeRow;
    setChangeRow(null);
    const alt = row.trending_alternative?.product?.id === p.id ? row.trending_alternative : null;
    onSwap?.(activeTier, row.element_index, {
      product: p,
      match_kind: alt?.match_kind || row.match_kind,
      delivery,
      is_trending: !!alt || !!p.is_trending,
      user_changed: true,
    });
  };

  const toggle = (idx) => setSelected((s) => ({ ...s, [idx]: !s[idx] }));

  return (
    <div className="space-y-6" data-testid="planner-result">
      <AIPlanIdentityHeader />
      {showReadyHeader && (
        <div className="rounded-xl bg-gradient-to-r from-emerald-50 to-rose-50 border border-emerald-200 px-3 py-2 flex items-center gap-2"
          data-testid="plan-ready-header">
          <span className="text-base">🎉</span>
          <p className="text-sm font-semibold text-charcoal">Your Celebration Plan is Ready</p>
        </div>
      )}

      {/* Hard ceiling miss: full plan couldn't be matched within budget even
          after cheaper re-matching. Booking is disabled for this plan. */}
      {planInvalid && (
        <div
          className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 sm:p-5"
          data-testid="budget-invalid-banner"
        >
          <div className="flex items-start gap-3">
            <span className="text-2xl flex-shrink-0" aria-hidden="true">⚠️</span>
            <div className="min-w-0 flex-1">
              <p className="font-display text-base sm:text-lg text-charcoal leading-snug">
                Some items in this plan couldn&apos;t be matched within your budget
              </p>
              {(validation || planInvalid) && (
                <p className="text-xs sm:text-sm text-charcoal/70 mt-1" data-testid="budget-invalid-detail">
                  This plan totals ₹{Number(livePlanTotal).toLocaleString("en-IN")}
                  {" "}against your ₹{Number(budget.max).toLocaleString("en-IN")} ceiling
                  (₹{Number(liveOverBy).toLocaleString("en-IN")} over).
                </p>
              )}
              <div className="flex flex-col sm:flex-row gap-2 mt-3">
                <button
                  type="button"
                  onClick={openBudgetPanel}
                  data-testid="try-different-budget"
                  className="rounded-full px-5 py-2.5 text-sm font-bold text-white shadow-md shadow-rose-500/30 transition-all hover:-translate-y-0.5"
                  style={{ background: "linear-gradient(135deg, #E11D48 0%, #A855F7 100%)" }}
                >
                  Try a different budget
                </button>
                <button
                  type="button"
                  onClick={onViewOriginal}
                  data-testid="view-original-plans"
                  className="rounded-full px-5 py-2.5 text-sm font-bold border-2 border-charcoal/30 text-charcoal hover:border-charcoal transition-colors"
                >
                  {hasStandardResult ? "View original plans" : "Regenerate standard plans"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Re-match transparency: plan was auto-adjusted to fit the budget. */}
      {!planInvalid && isCustomBudget && validation?.rematched && validation?.within_budget && (
        <div
          className="rounded-2xl border border-emerald-200 bg-emerald-50/60 px-4 py-3 flex items-center gap-3"
          data-testid="budget-rematched-note"
        >
          <Check className="w-5 h-5 text-emerald-600 flex-shrink-0" strokeWidth={2.5} />
          <p className="text-xs sm:text-sm text-charcoal/80">
            We swapped in a few smarter picks so this plan fits your ₹{Number(budget.max).toLocaleString("en-IN")} budget —
            total ₹{Number(validation.plan_total).toLocaleString("en-IN")}.
          </p>
        </div>
      )}

      {/* Session toggle between plan sets — no regeneration, no browser nav.
          On custom: offer "← View Original Plans". On original (when a custom
          set exists): offer "View Custom Plans →". */}
      {isCustomBudget && hasStandardResult && (
        <button
          type="button"
          onClick={onViewOriginal}
          data-testid="switch-to-original-plans"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-rose-600 hover:text-rose-700 transition-colors"
        >
          ← View Original Plans
        </button>
      )}
      {!isCustomBudget && hasCustomResult && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onViewCustom}
            data-testid="switch-to-custom-plans"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-rose-600 hover:text-rose-700 transition-colors"
          >
            View Custom Plans →
          </button>
        </div>
      )}

      {/* Tier segmented control */}
      {isCustomBudget
        ? <CustomBudgetSlider plans={plans} activeTier={activeTier} onChange={setActiveTier} min={budget.min} max={budget.max} />
        : <TierSlider plans={plans} activeTier={activeTier} onChange={setActiveTier} />}

      {/* Plan header (title + personal note only; no AI concepts or estimates) */}
      <section data-testid="plan-products">
        {(ins.title || ins.personalized_message) && (
          <div className="rounded-2xl p-3.5 sm:p-4 bg-gradient-to-br from-rose-50 to-violet-50 border border-white/60 mb-3">
            {ins.title && <h3 className="font-display text-lg sm:text-xl text-charcoal leading-tight" data-testid="tier-title">{ins.title}</h3>}
            {ins.personalized_message && (
              <div className="flex items-start gap-2 mt-1">
                <p className="flex-1 font-display italic text-sm text-charcoal/80 leading-snug" data-testid="plan-personal-message">&ldquo;{ins.personalized_message}&rdquo;</p>
                <CopyMessageButton text={ins.personalized_message} />
              </div>
            )}
          </div>
        )}
        <div className="flex items-start justify-between gap-3 mb-2">
          <p className="text-sm font-semibold text-charcoal" data-testid="plan-instruction">
            Your plan is ready — swap any item or add something extra.
          </p>
          <p className="text-[11px] uppercase tracking-widest text-charcoal/60 flex items-center gap-1 flex-shrink-0 pt-1">
            <MapPin className="w-3 h-3" /> {pincode}
          </p>
        </div>
        <div className="space-y-2">
          {productRows.map((a) => (
            <PlanProductCard
              key={a.element_index}
              row={a}
              personal={personal[a.element_index]}
              included={!!selected[a.element_index]}
              onToggle={() => toggle(a.element_index)}
              onChange={() => setChangeRow(a)}
            />
          ))}
          {productRows.length === 0 && (
            <p className="text-sm text-charcoal/60 rounded-2xl border border-warm bg-white p-4" data-testid="plan-no-products">
              We couldn&apos;t match products for this plan — try another tier or add an experience below.
            </p>
          )}
        </div>
        <ChangeProductDialog row={changeRow} pincode={pincode} onPick={pickReplacement} onClose={() => setChangeRow(null)} />
      </section>

      {/* Independent experience add-ons — always shown from Admin config, regardless of AI output. */}
      {experiences && experiences.length > 0 && (
        <ExperienceAddOns
          experiences={experiences}
          selected={selectedExperiences}
          onToggle={onToggleExperience}
        />
      )}

      {/* Fallback: "Not happy with these options? Set your own budget" */}
      <CustomBudgetPanel
        activeBudget={budget}
        customMin={customMin} setCustomMin={setCustomMin}
        customMax={customMax} setCustomMax={setCustomMax}
        onRegenerate={onCustomBudgetRegenerate}
        loading={regenLoading}
        open={budgetPanelOpen} setOpen={setBudgetPanelOpen}
      />
    </div>
  );
}

function ExperienceAddOns({ experiences, selected, onToggle }) {
  const selectedTotal = experiences.reduce((s, e) => {
    if (!selected[e.id]) return s;
    if (e.price_type === "on_request") return s;
    return s + (e.price || 0);
  }, 0);
  const selectedCount = experiences.filter((e) => selected[e.id]).length;

  return (
    <section data-testid="experience-addons" className="pt-4">
      <div className="rounded-2xl p-3.5 sm:p-4 bg-gradient-to-br from-amber-50 via-rose-50 to-violet-50 border border-white/60">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold gradient-text-rose-purple mb-0.5">
              Add-ons
            </p>
            <h3 className="text-sm font-bold uppercase tracking-wider text-charcoal leading-tight">
              <span className="mr-1">✨</span> Make It More Special
            </h3>
            <p className="text-charcoal/60 text-xs mt-0.5">
              Optional experiences you can add to any celebration.
            </p>
          </div>
          {selectedCount > 0 && (
            <div className="text-right" data-testid="experience-addons-summary">
              <p className="text-[10px] uppercase tracking-widest text-charcoal/60">
                {selectedCount} add-on{selectedCount > 1 ? "s" : ""} selected
              </p>
              <p className="font-display text-xl text-rose-600 font-bold">
                {selectedTotal > 0 ? `+ ₹${selectedTotal.toLocaleString("en-IN")}` : "Price on request"}
              </p>
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          {experiences.map((e) => (
            <ExperienceCard
              key={e.id}
              experience={e}
              checked={!!selected[e.id]}
              onToggle={() => onToggle(e.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function ExperienceCard({ experience: e, checked, onToggle }) {
  const priceLabel =
    e.price_type === "on_request"
      ? "Price on request"
      : e.price_type === "approx"
      ? `~ ₹${(e.price || 0).toLocaleString("en-IN")}`
      : `₹${(e.price || 0).toLocaleString("en-IN")}`;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); onToggle(); } }}
      data-testid={`experience-card-${e.id}`}
      className={`cursor-pointer text-left rounded-2xl border-2 bg-white overflow-hidden transition-all ${
        checked ? "border-rose-500 shadow-md ring-2 ring-rose-200" : "border-warm hover:border-charcoal/30"
      }`}
    >
      <div className="flex items-stretch gap-3">
        <div className="w-20 sm:w-24 flex-shrink-0 self-stretch min-h-[5rem] bg-linen-2 overflow-hidden">
          {e.image_url ? (
            <img src={e.image_url} alt={e.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-charcoal/30">
              <PartyPopper className="w-8 h-8" strokeWidth={1.5} />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0 py-2.5 pr-3 flex flex-col justify-between">
          <div className="min-w-0">
            <div className="flex items-start justify-between gap-2">
              <p className="font-semibold text-charcoal text-sm leading-snug break-words">
                {e.name}
              </p>
              <TickBox
                checked={checked}
                onToggle={(ev) => { if (ev && ev.stopPropagation) ev.stopPropagation(); onToggle(); }}
                testId={`experience-check-${e.id}`}
              />
            </div>
            <p className="text-xs text-charcoal/60 mt-0.5 leading-snug">{e.description}</p>
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <span
              className={`text-[10px] uppercase tracking-[0.15em] font-bold ${
                e.price_type === "on_request" ? "text-violet-600" : "text-rose-600"
              }`}
              data-testid={`experience-price-${e.id}`}
            >
              {priceLabel}
            </span>
            {e.price_type === "approx" && (
              <span className="text-[10px] text-charcoal/50">final quote at booking</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Sum of real inventory prices in a plan (+ one delivery fee).
function matchedTotal(plan) {
  let sum = 0;
  let fee = 0;
  (plan.availability || []).forEach((a) => {
    if (a.product && a.match_kind !== "sourceable" && a.match_kind !== "none") {
      sum += Number(a.product.price || 0);
      fee = Math.max(fee, Number(a.delivery?.fee || 0));
    }
  });
  return sum + fee;
}

function TierSlider({ plans, activeTier, onChange }) {
  // Ensure a stable Essential → Experience → Grand order
  const order = ["Essential", "Experience", "Grand"];
  const sorted = [...plans].sort((a, b) => order.indexOf(a.tier) - order.indexOf(b.tier));
  const activeIdx = Math.max(0, sorted.findIndex((p) => p.tier === activeTier));

  return (
    <div data-testid="tier-slider" className="rounded-2xl border border-warm bg-white p-3 sm:p-4">
      <div className="flex items-center justify-between mb-3 gap-2">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-charcoal/60 flex-shrink-0">Budget tier</p>
        <p className="text-xs text-charcoal/60 truncate">
          {TIER_META[activeTier]?.short}
        </p>
      </div>
      <div className="grid grid-cols-3 gap-1 sm:gap-3">
        {sorted.map((p) => {
          const isActive = p.tier === activeTier;
          const budget = matchedTotal(p);
          return (
            <button
              key={p.tier}
              onClick={() => onChange(p.tier)}
              data-testid={`tier-tab-${p.tier}`}
              className={`rounded-xl px-1 py-2 sm:px-3 sm:py-3 text-center sm:text-left transition-all border-2 min-w-0 ${
                isActive
                  ? "border-rose-500 bg-rose-50 shadow-md"
                  : "border-warm bg-white hover:border-charcoal/30"
              }`}
            >
              <p className={`text-[9px] leading-tight tracking-tight sm:text-[11px] sm:tracking-[0.15em] uppercase font-bold ${isActive ? "text-rose-600" : "text-charcoal/50"}`}>
                {p.tier}
              </p>
              <p className={`font-display text-xs sm:text-lg mt-0.5 truncate leading-tight ${isActive ? "text-charcoal" : "text-charcoal/80"}`}>
                ₹{budget.toLocaleString("en-IN")}
              </p>
            </button>
          );
        })}
      </div>
      {/* Slider track visual */}
      <div className="mt-3 h-1.5 rounded-full bg-linen-2 relative overflow-hidden">
        <div
          className="absolute top-0 left-0 h-full rounded-full transition-all"
          style={{
            width: `${((activeIdx + 1) / sorted.length) * 100}%`,
            background: "linear-gradient(90deg, #E11D48 0%, #A855F7 100%)",
          }}
        />
      </div>
    </div>
  );
}

function CustomBudgetSlider({ plans, activeTier, onChange, min, max }) {
  // Custom Budget: hide the fixed-price "Budget tier" slider and instead
  // show the customer's chosen range as a hero banner + a lighter 3-tab
  // switcher labeled with each plan's adapted estimated_budget.
  const order = ["Essential", "Experience", "Grand"];
  const sorted = [...plans].sort((a, b) => order.indexOf(a.tier) - order.indexOf(b.tier));
  const activeIdx = Math.max(0, sorted.findIndex((p) => p.tier === activeTier));

  return (
    <div data-testid="custom-budget-slider" className="space-y-3">
      {/* Hero banner — the customer's custom range */}
      <div
        className="rounded-2xl px-4 py-3 sm:px-5 sm:py-4 border border-rose-200 bg-gradient-to-r from-rose-50 via-white to-violet-50 flex items-center gap-3"
        data-testid="custom-budget-banner"
      >
        <span className="text-2xl flex-shrink-0" aria-hidden="true">🎯</span>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-charcoal/60">Your Budget</p>
          <p className="font-display text-lg sm:text-xl text-charcoal leading-tight truncate">
            ₹{Number(min).toLocaleString("en-IN")}–₹{Number(max).toLocaleString("en-IN")}
          </p>
        </div>
      </div>

      {/* Adapted-plan tabs */}
      <div className="rounded-2xl border border-warm bg-white p-3 sm:p-4">
        <div className="grid grid-cols-3 gap-1 sm:gap-3">
          {sorted.map((p) => {
            const isActive = p.tier === activeTier;
            const est = matchedTotal(p);
            return (
              <button
                key={p.tier}
                onClick={() => onChange(p.tier)}
                data-testid={`custom-tier-tab-${p.tier}`}
                className={`rounded-xl px-1 py-2 sm:px-3 sm:py-3 text-center sm:text-left transition-all border-2 min-w-0 ${
                  isActive
                    ? "border-rose-500 bg-rose-50 shadow-md"
                    : "border-warm bg-white hover:border-charcoal/30"
                }`}
              >
                <p className={`text-[9px] leading-tight tracking-tight sm:text-[11px] sm:tracking-[0.15em] uppercase font-bold ${isActive ? "text-rose-600" : "text-charcoal/50"}`}>
                  {p.tier}
                </p>
                <p className={`font-display text-xs sm:text-lg mt-0.5 truncate leading-tight ${isActive ? "text-charcoal" : "text-charcoal/80"}`}>
                  ₹{est.toLocaleString("en-IN")}
                </p>
              </button>
            );
          })}
        </div>
        {/* Slider track visual */}
        <div className="mt-3 h-1.5 rounded-full bg-linen-2 relative overflow-hidden">
          <div
            className="absolute top-0 left-0 h-full rounded-full transition-all"
            style={{
              width: `${((activeIdx + 1) / sorted.length) * 100}%`,
              background: "linear-gradient(90deg, #E11D48 0%, #A855F7 100%)",
            }}
          />
        </div>
      </div>
    </div>
  );
}

function CustomBudgetPanel({ activeBudget, customMin, setCustomMin, customMax, setCustomMax, onRegenerate, loading, open, setOpen }) {
  const isCustom = activeBudget?.tier_key === "custom";
  const lo = Number(customMin);
  const hi = Number(customMax);
  const valid = lo > 0 && hi > 0 && hi >= lo;

  const submit = () => {
    if (!valid || loading) return;
    onRegenerate?.({ min: lo, max: hi });
  };

  // When we've already regenerated with a custom budget, keep the entry
  // point visible so the customer can quickly tweak the range again.
  const collapsedLabel = isCustom
    ? "Want to try a different budget?"
    : "Not happy with these options?";
  const ctaLabel = isCustom ? "Change budget" : "Set your own budget";

  return (
    <section
      data-testid="custom-budget-panel"
      className="rounded-2xl border border-dashed border-rose-300 bg-rose-50/40 p-4 sm:p-5"
    >
      {!open ? (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="min-w-0 flex-1">
            <p className="font-display text-base sm:text-lg text-charcoal leading-snug">
              {collapsedLabel}
            </p>
            <p className="text-xs sm:text-sm text-charcoal/60 mt-0.5">
              Enter your own budget and we&apos;ll design 3 fresh plans within your range.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="open-custom-budget"
            className="rounded-full px-5 py-3 text-sm font-bold border-2 border-rose-500 text-rose-600 hover:bg-rose-500 hover:text-white transition-colors whitespace-nowrap self-stretch sm:self-auto"
          >
            🎯 {ctaLabel}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-display text-base sm:text-lg text-charcoal leading-snug">
                🎯 Set your custom budget
              </p>
              <p className="text-xs sm:text-sm text-charcoal/60 mt-0.5">
                Total celebration budget range (INR).
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="close-custom-budget"
              className="rounded-full p-2 -m-2 text-charcoal/60 hover:bg-white flex-shrink-0"
              aria-label="Close custom budget"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-charcoal/50">Min ₹</span>
              <input
                type="number" inputMode="numeric" min="1"
                value={customMin}
                onChange={(e) => setCustomMin(e.target.value)}
                placeholder="3000"
                data-testid="custom-budget-min"
                className="mt-1 w-full rounded-xl border-2 border-warm bg-white px-3 h-12 sm:h-11 text-base sm:text-sm text-charcoal placeholder-charcoal/40 focus:outline-none focus:border-rose-500"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-charcoal/50">Max ₹</span>
              <input
                type="number" inputMode="numeric" min="1"
                value={customMax}
                onChange={(e) => setCustomMax(e.target.value)}
                placeholder="6000"
                data-testid="custom-budget-max"
                className="mt-1 w-full rounded-xl border-2 border-warm bg-white px-3 h-12 sm:h-11 text-base sm:text-sm text-charcoal placeholder-charcoal/40 focus:outline-none focus:border-rose-500"
              />
            </label>
          </div>

          {!valid && (customMin || customMax) && (
            <p className="text-xs text-rose-600" data-testid="custom-budget-error">
              Please enter a valid range (Max ≥ Min, both greater than 0).
            </p>
          )}

          <button
            type="button"
            onClick={submit}
            disabled={!valid || loading}
            data-testid="regenerate-custom-budget"
            className="w-full rounded-full h-12 sm:h-11 text-sm font-bold text-white shadow-lg shadow-rose-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ background: "linear-gradient(135deg, #E11D48 0%, #A855F7 100%)" }}
          >
            <Sparkles className="w-4 h-4" strokeWidth={2.25} />
            {loading ? "Designing new plans…" : "Regenerate with my budget"}
          </button>
        </div>
      )}
    </section>
  );
}



function TickBox({ checked, onToggle, disabled, testId }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className={`w-6 h-6 rounded-md border-2 flex-shrink-0 flex items-center justify-center transition-all ${
        disabled
          ? "border-charcoal/20 bg-charcoal/5 cursor-not-allowed"
          : checked
          ? "border-rose-500 bg-rose-500"
          : "border-warm hover:border-rose-400"
      }`}
      data-testid={testId}
    >
      {checked && !disabled && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
    </button>
  );
}

// -------- Legacy: no-match rows are now silently hidden --------
// Unsupported AI suggestions never reach the UI (backend drops them; the
// AvailabilityRow guard above skips any legacy cached `none` rows).

